import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BrandLogo } from "@/components/brand-logo";
import { GraduationCap, Users, Calendar, CreditCard, ArrowRight, BookOpen, Clock } from "lucide-react";
import { Link } from "wouter";
import { apiUrl } from "@/lib/api";

const features = [
  {
    icon: Users,
    title: "Student Management",
    description: "Keep track of all your students, their lesson preferences, schedules, and payment history in one organized place.",
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Set up recurring lessons with flexible scheduling. Manage online and offline sessions with address tracking.",
  },
  {
    icon: CreditCard,
    title: "Payment Tracking",
    description: "Never lose track of payments. Monitor pending and completed payments with detailed per-student debt summaries.",
  },
  {
    icon: BookOpen,
    title: "Lesson History",
    description: "Complete history of all lessons delivered. Filter by student, date, or payment status for full transparency.",
  },
  {
    icon: Clock,
    title: "Time & Rate Management",
    description: "Set custom hourly rates and currencies per student. Track lesson durations automatically.",
  },
  {
    icon: GraduationCap,
    title: "Professional Growth",
    description: "Focus on teaching while the app handles the admin. More organized means better teaching outcomes.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-6xl mx-auto px-6 min-h-[5.25rem] py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center min-w-0 flex-1">
            <BrandLogo variant="nav" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Button asChild data-testid="button-login-nav">
              <a href={apiUrl("/api/auth/google?returnTo=/")}>Sign In</a>
            </Button>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              Manage your
              <span className="text-primary"> students</span> & 
              <span className="text-primary"> payments</span> effortlessly
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
              The all-in-one platform for teachers to organize students, schedule lessons,
              and track payments. Spend less time on admin, more time teaching.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button size="lg" asChild data-testid="button-get-started">
                <a href={apiUrl("/api/auth/google?returnTo=/")} className="gap-2">
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
            </div>
            <div className="flex items-center gap-6 pt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Free
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                No credit card required
              </span>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative rounded-xl bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-8 border border-primary/10">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/80 border">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold text-sm">
                    SM
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Sarah Mitchell</p>
                    <p className="text-xs text-muted-foreground">Online - Tue & Thu 10:00</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Paid</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/80 border">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 font-semibold text-sm">
                    JK
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">James Kim</p>
                    <p className="text-xs text-muted-foreground">Offline - Mon & Wed 14:00</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">Pending</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-background/80 border">
                  <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-semibold text-sm">
                    EW
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Emma Wilson</p>
                    <p className="text-xs text-muted-foreground">Online - Fri 16:00</p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Paid</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold tracking-tight mb-3">Everything you need to manage your teaching</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Streamline your teaching business with tools designed specifically for language instructors.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature) => (
              <Card key={feature.title} className="p-6 hover-elevate" data-testid={`card-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold tracking-tight">Ready to get organized?</h2>
          <p className="text-muted-foreground">
            Join teachers who are saving hours every week on administration. 
            Start managing your students and payments in minutes.
          </p>
          <Button size="lg" asChild data-testid="button-get-started-bottom">
            <a href={apiUrl("/api/auth/google?returnTo=/")} className="gap-2">
              Start Today
              <ArrowRight className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </section>

      <footer className="border-t py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between gap-4 text-sm text-muted-foreground">
          <BrandLogo variant="footer" />
          <div className="flex flex-col sm:items-end gap-3">
            <nav className="flex flex-wrap items-center gap-x-4 gap-y-1" aria-label="Legal">
              <Link href="/privacy" className="hover:text-foreground underline-offset-4 hover:underline" data-testid="link-privacy-footer">
                Privacy Policy
              </Link>
              <Link href="/terms" className="hover:text-foreground underline-offset-4 hover:underline" data-testid="link-terms-footer">
                Terms of Service
              </Link>
            </nav>
            <p>&copy; {new Date().getFullYear()} TeachersHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
